import React from 'react'
import { isNull } from 'lodash'
import {
  Invoices_getInvoices_invoices,
  Invoices_getInvoices_pagination,
  SortDirectionEnum,
  getFormattedDateAndTime,
  TestType,
  SortByType,
  CategoryTable,
} from '@modmd/data'
import { Box } from 'components/Layout'
import Datalist, { DataListProps } from 'components/Datalist'
import Pagination from 'components/Pagination'
import Card from 'components/Card'
import { useIsMinDevice } from 'utils/hooks/useMedia'
import { DEVICE } from 'theme'
import { invoiceDateOrFail } from 'internal-portal/routes/FinancesList/utils'
import Label from 'components/Label'
import { ROUTES } from 'internal-portal/constants/routes'
import { useHistory } from 'react-router'

interface ColumnLabels {
  date?: string | null
  time?: string | null
  location?: string | null
  sublocation?: string | null
  numOfPeople?: string | null
  company?: string | null
  group?: string | null
  status?: string | null
  paymentStatus?: string | null
  invoiceCheck?: string | null
  invoiceDate?: string | null
  autoInvoice?: string | null
}

interface Props {
  tableHeader?: React.ReactElement
  tableFooter?: React.ReactElement
  invoices: Invoices_getInvoices_invoices[]
  sorter?: DataListProps['sorter']
  columnLabels?: ColumnLabels
  pagination?: Invoices_getInvoices_pagination
  isCard?: boolean
  isLoading?: boolean
  hasPaginationInFooter?: boolean
  onPageChange?: (page: number) => void
  onSortChange?: (sortBy: string, direction: SortDirectionEnum) => void
  pageLength?: number
  testSessionRefetch?: () => void
}

export const PAGE_LENGTH = 10

const defaultColumnLabels = {
  id: 'Id',
  date: 'Date',
  name: 'Name',
  location: 'Location',
  sublocation: 'Rate type',
  group: 'Group',
  paymentStatus: 'Invoice Number',
  invoiceCheck: 'Invoice',
  invoiceDate: null,
  autoInvoce: null,
}

export const FinancesDatalist: React.VFC<Props> = ({
  tableHeader,
  tableFooter,
  invoices,
  sorter,
  pagination,
  columnLabels: providedColumnLabels,
  isCard = true,
  isLoading,
  hasPaginationInFooter = false,
  onSortChange,
  onPageChange,
  pageLength = PAGE_LENGTH,
}) => {
  const history = useHistory()
  const isDesktop = useIsMinDevice(DEVICE.DESKTOP)
  const isTablet = useIsMinDevice(DEVICE.TABLET)
  const columnLabels = { ...defaultColumnLabels, ...(providedColumnLabels || {}) }
  const numOfColumns = Object.values(columnLabels).filter((label) => label !== null).length
  const rowGridTemplateColumns = React.useMemo(() => `repeat(${numOfColumns}, 1fr)`, [numOfColumns])
  const paginationComp = pagination && pagination.total > pageLength && onPageChange && (
    <Pagination
      firstPage={1}
      lastPage={Math.ceil(pagination.total / pageLength) || 1}
      currentPage={Math.ceil(pagination.from / pageLength) + 1}
      onPageChange={onPageChange}
    />
  )

  const Wrapper = isCard ? Card : Box
  return (
    <>
      <Wrapper>
        <Datalist
          isLoading={isLoading}
          skeletonProps={{
            columns: numOfColumns,
            rows: pageLength,
          }}
          data={invoices}
          sorter={sorter}
          onSortChange={onSortChange}
          header={tableHeader}
          footer={hasPaginationInFooter && paginationComp ? paginationComp : tableFooter}
          shouldRenderTableHeader={isDesktop}
          showActionIndicators
          renderTableHeader={
            isDesktop
              ? () => (
                  <Datalist.Row gridTemplateColumns={rowGridTemplateColumns}>
                    {!isNull(columnLabels.id) ? (
                      <Datalist.HeaderCell label={columnLabels.id} />
                    ) : null}
                    {!isNull(columnLabels.date) ? (
                      <Datalist.HeaderCell label={columnLabels.date} />
                    ) : null}
                    {!isNull(columnLabels.name) ? (
                      <Datalist.HeaderCell label={columnLabels.name} />
                    ) : null}
                    {!isNull(columnLabels.location) ? (
                      <Datalist.HeaderCell label={columnLabels.location} />
                    ) : null}
                    {!isNull(columnLabels.sublocation) ? (
                      <Datalist.HeaderCell label={columnLabels.sublocation} />
                    ) : null}
                    {!isNull(columnLabels.group) ? (
                      <Datalist.HeaderCell label={columnLabels.group} />
                    ) : null}
                    {!isNull(columnLabels.paymentStatus) ? (
                      <Datalist.HeaderCell
                        label={columnLabels.paymentStatus}
                        sortKey={SortByType.INVOICE_DOC_NUMBER}
                      />
                    ) : null}
                    {!isNull(columnLabels.autoInvoice) ? (
                      <Datalist.HeaderCell label={columnLabels?.autoInvoice} />
                    ) : null}
                    {!isNull(columnLabels.invoiceDate) ? (
                      <Datalist.HeaderCell label={columnLabels?.invoiceDate} />
                    ) : null}
                  </Datalist.Row>
                )
              : undefined
          }
          renderTableRow={(data) => {
            const {
              testSessionId,
              eventId,
              TestSession,
              Event,
              invoiceDocNumber,
              invoiceDate,
              autoInvoice,
            } = data
            const type = TestSession?.type ?? Event?.testType

            return (
              <Datalist.Row
                gridTemplateColumns={{
                  _: 'repeat(2, 1fr)',
                  DESKTOP: rowGridTemplateColumns,
                }}
                onClick={() => {
                  // eslint-disable-next-line @typescript-eslint/no-unused-expressions
                  testSessionId
                    ? history.push(`/${ROUTES.SESSIONS}/${String(testSessionId)}`)
                    : history.push(`/${ROUTES.EVENTS}/${String(eventId)}`)
                }}
                {...(!isTablet ? { alignItems: 'start' } : {})}
              >
                {!isNull(columnLabels.id) ? (
                  <Datalist.Cell label={columnLabels.id}>
                    <Label indicator={testSessionId ? 'green' : 'grayDark'}>
                      {testSessionId
                        ? `Test Session: ${testSessionId}`
                        : `Event: ${eventId as string}`}
                    </Label>
                  </Datalist.Cell>
                ) : null}
                {!isNull(columnLabels.date) ? (
                  <Datalist.Cell label={columnLabels.date}>
                    {getFormattedDateAndTime(TestSession?.date) ??
                      getFormattedDateAndTime(Event!.EventDays![0]!.startTime)}
                  </Datalist.Cell>
                ) : null}
                {!isNull(columnLabels.name) ? (
                  <Datalist.Cell label={columnLabels.name}>
                    {type === TestType.SofiaRsv && <Label indicator="grayDark">RSV</Label>}
                    {type === TestType.RapidCovid19 && <Label indicator="grayDark">Rapid</Label>}
                    {type === TestType.Covid19 && <Label indicator="grayDark">PCR</Label>}
                    {(type === TestType.Antigen ||
                      type === TestType.AntigenCareStart ||
                      type === TestType.SiennaAntigen ||
                      type === TestType.IndicaidAntigen) && (
                      <Label indicator="grayDark">Antigen</Label>
                    )}
                    {type === TestType.Lucira && <Label indicator="grayDark">Lucira</Label>}
                    {CategoryTable[type as keyof typeof CategoryTable] === 'Strep' && (
                      <Label indicator="grayDark">Strep</Label>
                    )}
                    {CategoryTable[type as keyof typeof CategoryTable] === 'Influenza' && (
                      <Label indicator="grayDark">Influenza</Label>
                    )}
                    <Box>{TestSession?.name ?? Event?.name}</Box>
                  </Datalist.Cell>
                ) : null}
                {!isNull(columnLabels.location) ? (
                  <Datalist.Cell label={columnLabels.location}>
                    {testSessionId
                      ? `${TestSession?.Location?.zip ?? ''}, ${
                          TestSession?.Location?.street ?? ''
                        }, ${TestSession?.Location?.city ?? ''} - ${
                          TestSession?.Location?.state ?? ''
                        }`
                      : `${Event?.Address?.zip ?? ''}, ${Event?.Address?.street ?? ''}, ${
                          Event?.Address?.city ?? ''
                        } - ${Event?.Address?.state ?? ''}`}
                  </Datalist.Cell>
                ) : null}
                {!isNull(columnLabels.sublocation) ? (
                  <Datalist.Cell label={columnLabels.sublocation}>
                    {TestSession?.sublocation ?? Event?.sublocation}
                  </Datalist.Cell>
                ) : null}
                {!isNull(columnLabels.group) ? (
                  <Datalist.Cell label={columnLabels.group}>
                    {testSessionId
                      ? `${TestSession?.Group?.name ?? ''} (${
                          TestSession?.Group?.Company?.name ?? ''
                        })`
                      : `${Event?.Group?.name ?? ''} (${Event?.Group?.Company?.name ?? ''})`}
                  </Datalist.Cell>
                ) : null}
                {!isNull(columnLabels.paymentStatus) ? (
                  <Datalist.Cell label={columnLabels.paymentStatus}>
                    <Label indicator={invoiceDocNumber ? 'lightGreen' : 'red'}>
                      {invoiceDocNumber ? `No: ${invoiceDocNumber}` : 'Failed'}
                    </Label>
                  </Datalist.Cell>
                ) : null}
                {!isNull(columnLabels.autoInvoice) && columnLabels.autoInvoice ? (
                  <Datalist.Cell label={columnLabels.autoInvoice}>
                    {autoInvoice ? 'Automatic' : 'Manual'}
                  </Datalist.Cell>
                ) : null}
                {!isNull(columnLabels.invoiceDate) && columnLabels.invoiceDate ? (
                  <Datalist.Cell label={columnLabels.invoiceDate}>
                    {invoiceDateOrFail(invoiceDate)}
                  </Datalist.Cell>
                ) : null}
              </Datalist.Row>
            )
          }}
        />
      </Wrapper>
      {!hasPaginationInFooter && paginationComp && (
        <Pagination.Wrapper>{paginationComp}</Pagination.Wrapper>
      )}
    </>
  )
}
